// OwnPBX customer documentation. Rendered by pages/DocsPage.jsx at /docs.
//
// Menu names, field labels, phone settings and API details here were taken
// from the OwnPBX console and backend, and SIP registration settings were
// verified against production. When the console changes, update the matching
// article — wrong instructions are worse than none.
//
// Block types: { p }, { list: [] }, { steps: [] }, { table: { head, rows } },
// { code }, { note }, { img: { src, alt, caption } }.
//
// Screenshots live in public/docs and are captured from a fictional demo
// company ("Acme Retail"), never a real customer's account. Re-capture them
// whenever the console's screens change.

export const docPath = (slug) => `/docs/${slug}`;

const CONSOLE = "https://console.ownpbx.in";
const SIP_SERVER = "console.ownpbx.in";

export const DOC_GROUPS = [
  { title: "Getting started", slugs: ["getting-started", "extensions", "connect-phones"] },
  { title: "Calls & routing", slugs: ["phone-numbers", "ivr-ring-groups-queues", "hours-voicemail-forwarding", "sip-trunks"] },
  { title: "AI voice agents", slugs: ["ai-agents"] },
  { title: "Monitoring & billing", slugs: ["reports", "billing"] },
  { title: "Developers", slugs: ["api"] },
  { title: "Help", slugs: ["troubleshooting"] },
];

export const DOCS = {
  // ───────────────────────────────────────────────────────────────────────────
  "getting-started": {
    title: "Getting started",
    summary: "Sign in, find your way around the console, and get your first call working.",
    sections: [
      {
        h: "Sign in",
        blocks: [
          { steps: [
            `Open ${CONSOLE}.`,
            "Choose Business and sign in with the admin email and password from your welcome email.",
            "Change your password: click your name at the top right and choose Change Password. This also signs you out on any other device.",
          ] },
          { note: "Team members who only take calls don't need an admin login. They sign in as Agent with their extension — see Connect phones and softphones." },
        ],
      },
      {
        h: "The two workspaces",
        blocks: [
          { p: "The console has two workspaces, switched from the toggle at the top of the menu:" },
          { table: { head: ["Workspace", "What it's for"], rows: [
            ["PBX", "Extensions, phone numbers, call routing, IVR, queues, recordings, reports and billing."],
            ["AI Agent", "Creating and testing AI voice agents, their knowledge base and voices, and reviewing their conversations. Shown only if AI agents are included in your plan."],
          ] } },
          { img: { src: "/docs/console-dashboard.webp", alt: "The OwnPBX console dashboard showing extensions, calls today, live channels and the 7-day call trend", caption: "The dashboard: your numbers at a glance, live calls and the last seven days." } },
        ],
      },
      {
        h: "Your first working call",
        blocks: [
          { steps: [
            "Create two extensions (PBX → Extensions).",
            "Connect a softphone or desk phone to each — or sign in to one as an Agent in the browser.",
            "Dial one extension from the other to confirm calling works.",
            "Point a phone number at an extension, IVR or queue (PBX → DID Numbers) to receive outside calls.",
          ] },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  extensions: {
    title: "Extensions",
    summary: "Create the internal numbers your team uses to make and receive calls.",
    sections: [
      {
        h: "Create an extension",
        blocks: [
          { steps: [
            "Go to PBX → Extensions and add an extension.",
            "Fill in the fields below and save.",
          ] },
          { table: { head: ["Field", "What to enter"], rows: [
            ["Extension", "The internal number, e.g. 1001. Must be unique in your account."],
            ["SIP Password", "Used by the phone to sign in. Use a long, random password — anyone with it can make calls on your account."],
            ["Display Name", "The person's name, shown to colleagues on internal calls."],
            ["Caller ID Number", "The number shown to people this extension calls outside the company."],
            ["Voicemail PIN", "Used to listen to voicemail from the phone."],
            ["Department", "Optional. Extensions in a department see only their own team."],
          ] } },
          { img: { src: "/docs/console-extensions.webp", alt: "The Extensions page listing each extension number, name, voicemail status and actions", caption: "Every extension in your account, with its SIP details behind Edit." } },
        ],
      },
      {
        h: "Departments",
        blocks: [
          { p: "Create departments under PBX → Departments and assign extensions to them. People in a department only see their own team's extensions and activity, which keeps larger organisations tidy and private." },
        ],
      },
      {
        h: "Limits",
        blocks: [
          { p: "Your plan sets the maximum number of extensions and simultaneous calls. If you reach a limit, contact us to upgrade." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "connect-phones": {
    title: "Connect phones and softphones",
    summary: "Settings for desk phones and softphone apps, and how agents take calls in the browser.",
    sections: [
      {
        h: "SIP settings",
        blocks: [
          { p: "Use these settings in any SIP desk phone or softphone app (for example Zoiper, MicroSIP or Linphone):" },
          { table: { head: ["Setting", "Value"], rows: [
            ["Username / Auth ID", "Your extension number, e.g. 1001"],
            ["Password", "The extension's SIP Password"],
            ["Domain / Realm", "Your account's SIP domain, shown as SIP Domain in your welcome email (e.g. acme.sip.ownpbx.in)"],
            ["Server / Outbound proxy", SIP_SERVER],
            ["Port", "5060"],
            ["Transport", "UDP"],
          ] } },
          { note: "Domain and server are different. The Domain must be your account's own SIP domain; console.ownpbx.in goes in the Server / Outbound proxy field. Entering console.ownpbx.in as the domain will be rejected." },
          { p: "Shortcut: if your SIP domain ends in .sip.ownpbx.in, you can enter your SIP domain in both the Domain and Server fields — it points to our server automatically." },
        ],
      },
      {
        h: "Take calls in the browser (Agents)",
        blocks: [
          { p: "Agents can make and receive calls in a web browser without installing anything:" },
          { steps: [
            `Open ${CONSOLE} and choose Agent.`,
            "Enter your Tenant (your company's short ID, e.g. acme), your Extension and its Password.",
            "Allow microphone access when your browser asks.",
          ] },
          { note: "Use a headset and a stable internet connection for the best call quality." },
          { img: { src: "/docs/console-agent-phone.webp", alt: "The agent portal in a browser, showing the team, quick dial and today's activity", caption: "The agent portal: no app to install — teammates, quick dial and call history in the browser." } },
        ],
      },
      {
        h: "Failed sign-in protection",
        blocks: [
          { p: "To protect accounts from password-guessing attacks, 5 failed phone sign-ins from the same internet address within 10 minutes block that address for 24 hours. This blocks every device on that network, including the web console." },
          { p: "If a phone keeps failing, fix its password before it retries. If your office gets blocked, email ops@ownpbx.in with your public IP address and we will unblock it." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "phone-numbers": {
    title: "Phone numbers",
    summary: "Route incoming calls on your numbers to extensions, IVRs, ring groups or queues.",
    sections: [
      {
        h: "Route a number",
        blocks: [
          { steps: [
            "Go to PBX → DID Numbers.",
            "Edit the number and choose where calls should go.",
            "Save. New calls follow the route immediately.",
          ] },
          { table: { head: ["Destination", "Use it when"], rows: [
            ["Extension", "Calls should ring one person."],
            ["IVR Menu", "Callers should choose an option, e.g. press 1 for Sales."],
            ["Ring Group", "Several phones should ring together or in order."],
            ["Call Queue", "Callers should wait in line for the next available agent."],
          ] } },
          { img: { src: "/docs/console-numbers.webp", alt: "The DID Numbers page showing each number and where incoming calls to it are sent", caption: "Each number and where its incoming calls go." } },
        ],
      },
      {
        h: "Getting numbers",
        blocks: [
          { p: "Numbers can be allocated to you by OwnPBX, or you can use numbers from your own carrier (see Bring your own carrier). Some numbers need identity or business documents before they can be activated under Indian telecom rules — we'll let you know what's required." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "ivr-ring-groups-queues": {
    title: "IVR, ring groups and queues",
    summary: "Build menus for callers, ring teams together, and queue callers for agents.",
    sections: [
      {
        h: "IVR / auto-attendant",
        blocks: [
          { p: "An IVR answers the call and plays options, like \"Press 1 for Sales, 2 for Support\"." },
          { steps: [
            "Go to PBX → Inbound Routing → IVR / Auto-Attendant and add an IVR menu.",
            "Enter a Menu Name and a Virtual Extension Number (the internal number for this menu, e.g. 825).",
            "Enter the Greeting — type the text and it will be spoken to callers.",
            "Add an option for each key and choose where it sends the caller.",
            "Route a phone number to the IVR (PBX → DID Numbers).",
          ] },
          { img: { src: "/docs/console-ivr.webp", alt: "An IVR menu with a greeting and keypad options pointing at queues and a ring group", caption: "An IVR menu: a greeting, and where each key sends the caller." } },
        ],
      },
      {
        h: "Ring groups",
        blocks: [
          { p: "A ring group rings several extensions from one number." },
          { table: { head: ["Field", "Meaning"], rows: [
            ["Name", "A label, e.g. Sales team."],
            ["Virtual Extension Number", "The internal number that rings the group."],
            ["Ring Strategy", "Ring All rings every member at once. Prioritized Hunt rings members one after another in priority order."],
            ["Ring Timeout (sec)", "How long to ring before giving up."],
            ["Destination if No Answer", "Where the call goes if nobody picks up."],
          ] } },
        ],
      },
      {
        h: "Call queues",
        blocks: [
          { p: "A queue holds callers until an agent is free, and tracks how long they waited. Add a queue under PBX → Inbound Routing → Call Queues, add the agents' extensions, then route a number to it." },
          { p: "Watch queues live in Analytics → Queue Wallboard." },
          { img: { src: "/docs/console-queues.webp", alt: "The Call Queues page showing queue name, extension, ring strategy and assigned agents", caption: "Queues, their ring strategy and the agents in them." } },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "hours-voicemail-forwarding": {
    title: "Business hours, voicemail and forwarding",
    summary: "Handle calls outside working hours, collect voicemail, and forward calls.",
    sections: [
      {
        h: "Business hours",
        blocks: [
          { p: "Go to PBX → Business Hours and add a schedule with your working days and times. Use it to send callers somewhere different when you're closed — for example to voicemail or an out-of-hours message." },
          { img: { src: "/docs/console-business-hours.webp", alt: "The Business Hours page with opening times and separate destinations for open and closed hours", caption: "Opening times, and where calls go outside them." } },
        ],
      },
      {
        h: "Voicemail",
        blocks: [
          { p: "Each extension can have voicemail, protected by its Voicemail PIN. Messages are listed under PBX → Voicemail." },
        ],
      },
      {
        h: "Call forwarding",
        blocks: [
          { p: "Forward an extension's calls to another number — always, when busy, or when unanswered — under PBX → Call Forwarding." },
        ],
      },
      {
        h: "Call recording",
        blocks: [
          { p: "Go to PBX → Recordings → Recording Settings → Configure to record inbound calls, outbound calls, or both. Recordings are listed on the same page." },
          { note: "You are responsible for informing callers that calls are recorded and obtaining any consent the law requires." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "sip-trunks": {
    title: "Bring your own carrier",
    summary: "Connect your existing SIP carrier or phone numbers to OwnPBX.",
    sections: [
      {
        h: "Add a SIP trunk",
        blocks: [
          { p: "If your plan includes bring-your-own-carrier, add your carrier under PBX → SIP Trunks." },
          { table: { head: ["Field", "What to enter"], rows: [
            ["Trunk Name", "A label, e.g. my-carrier-1."],
            ["SIP Host", "Your carrier's SIP server, e.g. sip.mycarrier.com."],
            ["Port", "Usually 5060."],
            ["Codecs", "The audio codecs your carrier supports."],
            ["Outbound Caller ID", "The number to present on outgoing calls, in the format your carrier expects."],
            ["Authentication Type", "Username and password if your carrier gave you credentials, or IP-based if your carrier authorises by address."],
            ["Allowed IP / Their Source IP", "For IP-based trunks: the address your carrier sends calls from."],
          ] } },
          { img: { src: "/docs/console-trunks.webp", alt: "The SIP Trunks page listing carrier trunks and their registration status", caption: "Your carrier trunks and whether each one is connected." } },
        ],
      },
      {
        h: "Before you start",
        blocks: [
          { list: [
            "Ask your carrier to allow calls from our server if they use IP authentication — email ops@ownpbx.in for the address.",
            "Make sure numbers you use as caller ID belong to you on that carrier; carriers reject calls with numbers they don't recognise.",
          ] },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  "ai-agents": {
    title: "AI voice agents",
    summary: "Create an AI agent that answers or makes calls in English, Hindi and Hinglish.",
    sections: [
      {
        h: "Create an agent",
        blocks: [
          { steps: [
            "Switch to the AI Agent workspace using the toggle at the top of the menu.",
            "Start the new-agent wizard and enter an Agent Name.",
            "Describe what the agent should do, or start from a template.",
            "Pick a voice. For Hindi and other Indian languages, use a voice from Indian (Sarvam).",
            "Finish the wizard, then refine the greeting and instructions in the agent's settings.",
          ] },
          { img: { src: "/docs/console-ai-agent.webp", alt: "The AI Voice Agents workspace showing conversations, average duration and resolution rate", caption: "The AI agents workspace: your agents on the left, results on the right." } },
        ],
      },
      {
        h: "Write good instructions",
        blocks: [
          { list: [
            "Say who the agent is, who it's calling or answering, and the goal of the call.",
            "Ask for short replies — one idea at a time. Callers can't interrupt while the agent is speaking, so long answers feel like being talked at.",
            "List the facts it may use (products, prices, timings) and what it must never promise.",
            "Tell it when to hand over to a person.",
            "Match the voice language to the language the agent speaks — a Hindi agent should use a Hindi voice.",
          ] },
        ],
      },
      {
        h: "Knowledge base",
        blocks: [
          { p: "Add product details, FAQs and policies under Knowledge Base. The agent looks up the relevant part during a call, so you don't need to paste everything into its instructions." },
        ],
      },
      {
        h: "Take calls and make calls",
        blocks: [
          { list: [
            "Inbound: assign a phone number to the agent under Phone Numbers. Calls to that number are answered by the agent.",
            "Outbound: use Outbound to have the agent call a number or run a calling campaign.",
          ] },
          { note: "Automated sales calls must follow TRAI telemarketing rules, including not calling numbers registered on the National Customer Preference Register without a valid basis." },
        ],
      },
      {
        h: "Review and improve",
        blocks: [
          { p: "Every call's transcript is available under Conversations. Read a few calls after any change, and use Tests to try the agent before putting it live." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  reports: {
    title: "Reports and wallboards",
    summary: "Track call volume, agent activity and individual calls.",
    sections: [
      {
        h: "Analytics",
        blocks: [
          { p: "PBX → Analytics has a tab for each report, all using the date range at the top:" },
          { table: { head: ["Tab", "Shows"], rows: [
            ["Summary", "Total, answered and missed calls, answer rate (ASR), average call length (ACD), cost, trends, busiest hours, and why calls failed."],
            ["Agent Performance", "Calls and talk time per agent."],
            ["Login / Logout, Break Time", "When agents signed in and how long breaks lasted."],
            ["Agent Wallboard", "Live status of every agent — on call, available, on break — with how long they've been in that state. Use TV mode for a wall display."],
            ["Queue Wallboard", "Callers waiting, agents ready, and today's answered and abandoned calls per queue."],
            ["Call Journey", "Pick any call to see each step it took — number, menu, queue, who answered and how it ended."],
          ] } },
          { img: { src: "/docs/console-analytics.webp", alt: "The analytics summary with total calls, answered, answer seizure ratio, talk minutes and call volume charts", caption: "Analytics summary: volume, answer rate, talk time and what each call cost." } },
          { img: { src: "/docs/console-agent-wallboard.webp", alt: "The agent wallboard showing each agent's state, time in state, occupancy and calls handled today", caption: "The agent wallboard, for watching the floor live." } },
        ],
      },
      {
        h: "Call records and live calls",
        blocks: [
          { list: [
            "Call Records (CDR): every call with its cost. Filter by date, direction and answered or unanswered, and export to CSV.",
            "Live Calls: calls in progress right now.",
            "Recordings: listen to or download recorded calls.",
          ] },
          { img: { src: "/docs/console-call-records.webp", alt: "The call records page listing calls with time, direction, from, to, talk time and result", caption: "Call records: every call with its result, searchable and exportable." } },
        ],
      },
      {
        h: "How \"answered\" is counted",
        blocks: [
          { p: "A call counts as answered when it was connected and carried audio. Missed calls are inbound calls nobody answered; outbound calls that never connected are shown separately as failed." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  billing: {
    title: "Billing and balance",
    summary: "See your rates, top up your balance and follow your charges.",
    sections: [
      {
        h: "My Account & Billing",
        blocks: [
          { p: "PBX → My Account & Billing shows your plan, your balance, Your Rates, your top-up requests, and Account Activity — every charge and top-up, page by page." },
          { img: { src: "/docs/console-billing.webp", alt: "The My Account and Billing page showing the current plan, wallet balance and top-up requests", caption: "Your plan, balance and account activity in one place." } },
        ],
      },
      {
        h: "How calls are charged",
        blocks: [
          { list: [
            "Only connected calls are charged. Unanswered and rejected calls are free.",
            "Calls are billed in pulses shown on Your Rates — for example 60/6 means the first minute is charged in full, then every 6 seconds after that.",
            "On prepaid plans the cost is deducted from your balance as each call ends, and appears against the call in Call Records.",
            "On plans with included minutes, calls stop once the minutes are used up.",
          ] },
        ],
      },
      {
        h: "Top up your balance",
        blocks: [
          { steps: [
            "Open My Account & Billing and use Request Balance Top-up.",
            "Enter the amount and submit.",
            "Track the request under My Top-up Requests; the balance updates once it's approved.",
          ] },
          { note: "If your balance runs out, outgoing calls stop until you top up. Refunds are covered by our Refund & Cancellation Policy." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  api: {
    title: "API and webhooks",
    summary: "Automate OwnPBX: manage extensions, fetch call records, start calls and receive call events.",
    sections: [
      {
        h: "Authentication",
        blocks: [
          { steps: [
            "Go to PBX → Developer API and create an API key.",
            "Copy the key straight away — it is shown only once.",
            "Send it with every request in the Authorization header.",
          ] },
          { code: `curl ${CONSOLE}/api/v1/me \\\n  -H "Authorization: Bearer YOUR_API_KEY"` },
          { note: "Keep API keys secret and server-side. Anyone with a key can act on your account. Delete a key immediately if it's exposed." },
          { img: { src: "/docs/console-developer-api.webp", alt: "The Developer API page where API keys are created and webhooks are configured", caption: "PBX → Developer API: create keys and point webhooks at your own server." } },
        ],
      },
      {
        h: "Endpoints",
        blocks: [
          { p: `All paths are relative to ${CONSOLE}.` },
          { table: { head: ["Method & path", "Purpose"], rows: [
            ["GET /api/v1/me", "Your account details."],
            ["GET /api/v1/extensions", "List extensions."],
            ["POST /api/v1/extensions", "Create an extension. Body: extension, password, caller_id_name, caller_id_number."],
            ["PUT /api/v1/extensions/:ext", "Update an extension."],
            ["DELETE /api/v1/extensions/:ext", "Delete an extension."],
            ["GET /api/v1/dids", "List phone numbers."],
            ["PUT /api/v1/dids/:id", "Change where a number routes."],
            ["GET /api/v1/calls", "Call records. Query: from, to (YYYY-MM-DD), direction, extension, search, limit (max 1000), offset."],
            ["GET /api/v1/calls/:uuid", "One call."],
            ["GET /api/v1/recordings", "List recordings."],
            ["GET /api/v1/recordings/:id/download", "Download a recording."],
            ["POST /api/v1/calls/c2c", "Click-to-call: rings your agent's extension first, then connects them to the destination. The destination is called through your account's normal outbound routing and billed like any other call. Body: agent_extension, destination, caller_id (optional — defaults to the extension's caller ID number)."],
            ["POST /api/v1/calls/originate", "Connect two internal extensions. Body: from, to, caller_id (optional)."],
            ["GET /api/v1/contacts, POST, DELETE /:id", "Manage phonebook contacts."],
            ["GET /api/v1/webhooks, POST, PATCH /:id, DELETE /:id", "Manage webhooks."],
          ] } },
        ],
      },
      {
        h: "Example: click-to-call",
        blocks: [
          { code: `curl -X POST ${CONSOLE}/api/v1/calls/c2c \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{"agent_extension":"1001","destination":"919876543210"}'` },
        ],
      },
      {
        h: "Webhooks",
        blocks: [
          { p: "Create a webhook to receive call events at your URL as they happen. Body when creating: name, url, events (comma-separated), secret (optional but recommended)." },
          { table: { head: ["Event", "Sent when"], rows: [
            ["call.started", "A call begins."],
            ["call.ended", "A call finishes."],
            ["call.missed", "An inbound call is not answered."],
            ["call.recorded", "A recording is ready."],
            ["agent.call.started", "An AI agent call begins."],
            ["agent.call.ended", "An AI agent call finishes."],
          ] } },
          { p: "Each request is a POST with a JSON body containing event, timestamp and data. Read the event name from the body." },
        ],
      },
      {
        h: "Verify webhook signatures",
        blocks: [
          { p: "If you set a secret, each request includes an X-OwnPBX-Signature header: sha256= followed by an HMAC-SHA256 of the raw request body using your secret. Reject requests where it doesn't match." },
          { code: `// Node.js (Express) — use the raw body, not parsed JSON\nconst crypto = require("crypto");\n\napp.post("/ownpbx-webhook", express.raw({ type: "application/json" }), (req, res) => {\n  const expected = "sha256=" + crypto\n    .createHmac("sha256", process.env.OWNPBX_WEBHOOK_SECRET)\n    .update(req.body)\n    .digest("hex");\n  const given = req.get("X-OwnPBX-Signature") || "";\n  const ok = given.length === expected.length &&\n    crypto.timingSafeEqual(Buffer.from(given), Buffer.from(expected));\n  if (!ok) return res.status(401).end();\n\n  const { event, data } = JSON.parse(req.body);\n  // handle event...\n  res.sendStatus(200);\n});` },
          { note: "Respond within 10 seconds, or the request is treated as failed." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  troubleshooting: {
    title: "Troubleshooting",
    summary: "Fixes for the most common problems.",
    sections: [
      {
        h: "My phone won't register",
        blocks: [
          { list: [
            `Check the Domain is your account's SIP domain and the Server / Outbound proxy is ${SIP_SERVER}. Swapping these is the most common mistake.`,
            "Check the username is the extension number and the password is its SIP Password.",
            "Use port 5060 over UDP.",
            "If it worked before and suddenly everything on your network stopped, your address may have been blocked after repeated failed sign-ins. Email ops@ownpbx.in with your public IP.",
          ] },
        ],
      },
      {
        h: "Outgoing calls fail",
        blocks: [
          { list: [
            "Check your balance or included minutes in My Account & Billing.",
            "Check the number format — include the country code, e.g. 91 for India.",
            "If you use your own carrier, confirm with them that calls from our server are allowed and your caller ID is valid on their network.",
            "Look up the call in Analytics → Call Journey to see where it stopped.",
          ] },
        ],
      },
      {
        h: "The AI agent doesn't answer",
        blocks: [
          { list: [
            "Make sure the phone number is assigned to the agent and the agent is enabled.",
            "Check your plan includes AI agents — the AI Agent workspace only appears when it does.",
            "Review the call under Conversations to see how far it got.",
          ] },
        ],
      },
      {
        h: "Contact support",
        blocks: [
          { p: "Email ops@ownpbx.in with your account name, the number or extension involved, and the time of the call. That lets us find it quickly." },
        ],
      },
    ],
  },
};
